<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1100">
    <v-card>
      <v-card-title class="dialog-title">
        {{ $t('org-dashboard.fw-drilldown-title') }}
        <template v-if="vendor"> — {{ vendor }} {{ model }}</template>
        <v-chip v-if="status === 'outdated'" x-small color="#f59e0b" text-color="white" class="ml-2">
          {{ $t('org-dashboard.fw-drilldown-outdated') }}
        </v-chip>
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 drilldown-body">
        <!-- KPI mini-cards -->
        <div v-if="summary" class="drilldown-kpi-cards">
          <div class="drilldown-kpi-card">
            <v-icon small color="#3b82f6" class="mr-1">mdi-ev-station</v-icon>
            <div class="drilldown-kpi-content">
              <span class="drilldown-kpi-value">{{ summary.total || 0 }}</span>
              <span class="drilldown-kpi-label">{{ $t('org-dashboard.fw-drilldown-total') }}</span>
            </div>
          </div>
          <div class="drilldown-kpi-card">
            <v-icon small color="#22c55e" class="mr-1">mdi-check-circle</v-icon>
            <div class="drilldown-kpi-content">
              <span class="drilldown-kpi-value">{{ summary.upToDate || 0 }}</span>
              <span class="drilldown-kpi-label">{{ $t('org-dashboard.fw-drilldown-up-to-date') }}</span>
            </div>
          </div>
          <div class="drilldown-kpi-card">
            <v-icon small color="#f59e0b" class="mr-1">mdi-alert</v-icon>
            <div class="drilldown-kpi-content">
              <span class="drilldown-kpi-value">{{ summary.outdated || 0 }}</span>
              <span class="drilldown-kpi-label">{{ $t('org-dashboard.fw-drilldown-outdated') }}</span>
            </div>
          </div>
        </div>

        <!-- Version tabs -->
        <v-tabs v-model="activeTab" background-color="transparent" class="drilldown-tabs" @change="onTabChange">
          <v-tab class="drilldown-tab">
            {{ $t('org-dashboard.fw-drilldown-all-versions') }}
          </v-tab>
          <v-tab v-for="ver in versionTabs" :key="ver.version" class="drilldown-tab">
            <span class="drilldown-tab-dot" :style="{ backgroundColor: ver.isLatest ? '#22c55e' : '#f59e0b' }"></span>
            {{ ver.version }}
            <span class="drilldown-tab-count">{{ ver.count }}</span>
          </v-tab>
        </v-tabs>

        <!-- Data table -->
        <div class="drilldown-table-wrap">
          <v-data-table
            :headers="tableHeaders"
            :items="items"
            :server-items-length="totalItems"
            :options.sync="internalOptions"
            :loading="loading"
            :no-data-text="$t('org-dashboard.health-no-data')"
            :footer-props="{
              'items-per-page-options': [10, 20, 50],
            }"
            @update:options="onOptionsChange"
            class="drilldown-table"
          >
            <template v-slot:item.identifier="{ item }">
              <a class="drilldown-link" @click.stop="$emit('charger-click', item.chargingPointId || item.id)">{{ item.identifier || item.name || '–' }}</a>
            </template>

            <template v-slot:item.locationName="{ item }">
              {{ item.locationName || item.location || '–' }}
            </template>

            <template v-slot:item.firmwareVersion="{ item }">
              <v-chip x-small :color="item.isLatest ? '#ecfdf5' : '#fffbeb'" :text-color="item.isLatest ? '#059669' : '#d97706'">
                {{ item.firmwareVersion || '–' }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="statusColor(item.status)" text-color="white">
                {{ item.status }}
              </v-chip>
            </template>

            <template v-slot:item.lastConnection="{ item }">
              {{ formatDate(item.lastConnection) }}
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'DashboardFirmwareDrilldown',
  props: {
    value: { type: Boolean, default: false },
    vendor: { type: String, default: null },
    model: { type: String, default: null },
    version: { type: String, default: null },
    status: { type: String, default: 'all' },
    summary: { type: Object, default: null },
    items: { type: Array, default: () => [] },
    totalItems: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      activeTab: 0,
      internalOptions: { page: 1, itemsPerPage: 20 },
    }
  },

  computed: {
    versionTabs() {
      return (this.summary && this.summary.versions) || []
    },

    tableHeaders() {
      return [
        { text: this.$t('org-dashboard.health-identifier'), value: 'identifier' },
        { text: this.$t('org-dashboard.fw-drilldown-location'), value: 'locationName' },
        { text: this.$t('org-dashboard.fw-drilldown-version'), value: 'firmwareVersion' },
        { text: this.$t('org-dashboard.health-connected'), value: 'status', sortable: false },
        { text: this.$t('org-dashboard.fw-drilldown-last-connection'), value: 'lastConnection' },
      ]
    },
  },

  watch: {
    version: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.activeTab = 0
        } else {
          const idx = this.versionTabs.findIndex(v => v.version === val)
          this.activeTab = idx >= 0 ? idx + 1 : 0
        }
      },
    },
  },

  methods: {
    onTabChange(tabIndex) {
      if (tabIndex === 0) {
        if (this.version !== null) {
          this.$emit('update:version', null)
        }
      } else {
        const ver = this.versionTabs[tabIndex - 1]
        if (ver && ver.version !== this.version) {
          this.$emit('update:version', ver.version)
        }
      }
    },

    onOptionsChange(options) {
      this.internalOptions = options
      this.$emit('update:options', options)
    },

    statusColor(status) {
      const map = {
        AVAILABLE: '#22c55e',
        CHARGING: '#3b82f6',
        PREPARING: '#8b5cf6',
        FINISHING: '#6366f1',
        RESERVED: '#eab308',
        UNAVAILABLE: '#f59e0b',
        FAULTED: '#ef4444',
        OFFLINE: '#6b7280',
      }
      return map[status] || '#9ca3af'
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
}

.drilldown-body {
  height: 70vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.drilldown-kpi-cards {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  background: #f9fafb;
  flex-shrink: 0;
}

.drilldown-kpi-card {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  flex: 1;
  min-width: 0;
}

.drilldown-kpi-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.drilldown-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.drilldown-kpi-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drilldown-tabs {
  flex-shrink: 0;
  flex-grow: 0;

  ::v-deep .v-tabs-bar {
    background: #f9fafb !important;
    border-bottom: 1px solid #e5e7eb;
  }

  ::v-deep .v-tab {
    text-transform: none;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    min-width: 100px;
  }

  ::v-deep .v-tab--active {
    font-weight: 600;
  }
}

.drilldown-tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
}

.drilldown-tab-count {
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 1px 6px;
  margin-left: 4px;
}

.drilldown-table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ::v-deep .drilldown-table.v-data-table {
    flex: 1;
    min-height: 0;
    display: flex !important;
    flex-direction: column !important;
  }

  ::v-deep .v-data-table__wrapper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  ::v-deep thead th {
    position: sticky !important;
    top: 0;
    z-index: 1;
    background: #f9fafb !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    color: #6b7280 !important;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    border-bottom: 1px solid #e5e7eb !important;
    padding: 8px 10px !important;

    span {
      font-size: 11px !important;
      font-weight: 600 !important;
    }
  }

  ::v-deep tbody td {
    font-size: 12px !important;
    color: #374151;
    border-bottom: 1px solid #f3f4f6 !important;
    padding: 6px 10px !important;
  }

  ::v-deep tbody tr:hover {
    background: #f9fafb !important;
  }

  ::v-deep tbody tr:last-child td {
    border-bottom: none !important;
  }

  ::v-deep .v-data-table__empty-wrapper {
    vertical-align: top;

    td {
      padding: 48px 16px !important;
      color: #9ca3af;
      font-size: 13px;
    }
  }

  ::v-deep .v-data-footer {
    flex-shrink: 0;
    border-top: 1px solid #e5e7eb;
    font-size: 12px;
  }
}

.drilldown-link {
  color: #1976d2;
  text-decoration: none;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: #1565c0;
  }
}
</style>
